/**
 * The editor's breadcrumb: back to the list WITH the filters and page the admin left it on. The
 * templates store still holds them (the `assessments` middleware wrote them on the way in and
 * nothing clears them on leave), so the link is rebuilt from the store rather than from history —
 * a deep link into the editor has no history to go back to and gets the plain list.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { LinkBreadcrumbItem } from '@shared/ui/menus'

import { AppRoute } from '@shared/config'

import { useAssessmentTemplatesStore } from '../assessment-templates/store'
import { useAssessmentEditorStore } from './store'

/** The API default (contract §1) — omitted from the link, exactly as the list omits it. */
const DEFAULT_PER_PAGE = 25

interface HeaderAssessmentEditorReturn {
  config: ComputedRef<LinkBreadcrumbItem[]>
}

/** `/content/assessments?grade=K4&status=DRAFT&page=2` — the same keys the list pushes, in the same spelling. */
export const getAssessmentListPath = (store: ReturnType<typeof useAssessmentTemplatesStore>): string => {
  const params = new URLSearchParams()

  if (store.getFilterSearch) params.set('search', store.getFilterSearch)
  if (store.getFilterType) params.set('type', store.getFilterType)
  if (store.getFilterDisciplineCode) params.set('discipline_code', store.getFilterDisciplineCode)
  if (store.getFilterGrade) params.set('grade', store.getFilterGrade)
  if (store.getFilterStatus) params.set('status', store.getFilterStatus)
  if (store.getPerPage !== DEFAULT_PER_PAGE) params.set('per_page', String(store.getPerPage))
  if (store.getCurrentPage > 1) params.set('page', String(store.getCurrentPage))

  const query = params.toString()

  return query ? `${AppRoute.Assessments}?${query}` : AppRoute.Assessments
}

export const useHeaderAssessmentEditor = (): HeaderAssessmentEditorReturn => {
  const templatesStore = useAssessmentTemplatesStore()
  const editorStore = useAssessmentEditorStore()

  const config = computed<LinkBreadcrumbItem[]>(() => [
    {
      id: 'assessment-templates',
      text: 'Assessment templates',
      to: getAssessmentListPath(templatesStore),
      disabled: false,
    },
    {
      id: 'assessment-template',
      // The row title, once loaded; a neutral label while the document is still on its way.
      text: editorStore.getTitle || 'Assessment template',
      to: null,
      disabled: true,
    },
  ])

  return {
    config,
  }
}
